import React, { useEffect, useId, useRef, useState } from "react";
import { Search } from "lucide-react";
import { ambassadors } from "../services/ambassadors";
import {
  AmbassadorPerson,
  ambassadorError,
  ambassadorPerson,
} from "../types/ambassadors";

export default function AmbassadorUserPicker({
  name,
  defaultValue = "",
  defaultLabel = "حساب متصل‌شده",
  required = false,
}: {
  name: string;
  defaultValue?: string;
  defaultLabel?: string;
  required?: boolean;
}) {
  const [value, setValue] = useState(defaultValue);
  const [label, setLabel] = useState(defaultLabel);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<(AmbassadorPerson & { phone?: string })[]>(
    [],
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const request = useRef(0);
  const id = useId();
  useEffect(
    () => () => {
      request.current++;
    },
    [],
  );
  const lookup = async () => {
    if (search.trim().length < 2) return;
    const version = ++request.current;
    setBusy(true);
    setError("");
    try {
      const rows = await ambassadors.directory(search);
      if (version === request.current) {
        setUsers(rows);
        setSearched(true);
      }
    } catch (e) {
      if (version === request.current) setError(ambassadorError(e));
    } finally {
      if (version === request.current) setBusy(false);
    }
  };
  return (
    <div className="amb-user-picker">
      <select
        name={name}
        value={value}
        required={required}
        aria-label="کاربر انتخاب‌شده"
        onChange={(e) => setValue(e.target.value)}
      >
        <option value="">کاربری انتخاب نشده است</option>
        {value && <option value={value}>{label}</option>}
      </select>
      <div className="amb-row">
        <input
          aria-label="جست‌وجوی کاربر سایت"
          aria-controls={id}
          placeholder="نام، تلفن یا نام کاربری"
          maxLength={100}
          value={search}
          onChange={(e) => {
            request.current++;
            setBusy(false);
            setSearch(e.target.value);
            setSearched(false);
            setUsers([]);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              lookup();
            }
          }}
        />
        <button
          type="button"
          disabled={busy || search.trim().length < 2}
          className="amb-secondary"
          onClick={lookup}
        >
          <Search size={16} />
          {busy ? "جست‌وجو…" : "جست‌وجو"}
        </button>
      </div>
      {error && (
        <p role="alert" className="amb-error">
          {error}
        </p>
      )}
      <div id={id} aria-live="polite">
        {searched && !users.length && (
          <p className="amb-muted">
            کاربری پیدا نشد. نام یا شماره دیگری را جست‌وجو کنید.
          </p>
        )}
        {users.map((user) => (
          <button
            type="button"
            key={user.id}
            className="amb-secondary amb-user-result"
            onClick={() => {
              setValue(user.id);
              setLabel(ambassadorPerson(user));
              setUsers([]);
              setSearched(false);
              setSearch("");
            }}
          >
            {ambassadorPerson(user)}{" "}
            <small dir="ltr">{user.phone || user.username}</small>
          </button>
        ))}
      </div>
    </div>
  );
}
